import { ChevronDown, ChevronRight } from '@atom-learning/icons'
import type { Row } from '@tanstack/react-table'
import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'

import { Icon } from '../icon/Icon'
import { Table } from '../table/Table'
import { useDataTable } from './DataTableContext'
import { DataTableDataCell } from './DataTableDataCell'
import { DataTableRowSelectionCheckbox } from './DataTableRowSelectionCheckbox'

export type DataTableRowProps = React.ComponentProps<typeof Table.Row> & {
  row: Row<Record<string, unknown>>
  rowAction?: (row: Record<string, unknown>, event: React.MouseEvent) => void
}

const StyledRow = styled(Table.Row, {
  base: ['bg-[initial]', 'relative'],
  variants: {
    isSelected: {
      true: ['bg-primary-100!']
    },
    isDisabled: {
      true: ['opacity-30']
    },
    isFocusable: {
      true: [
        'translate-y-0',
        'duration-200',
        'ease-out',
        'transition-shadow',
        'transition-transform',
        'hover:shadow-[0px_4px_11px_0px_hsla(0,0%,12%,0.12)]',
        'hover:-translate-y-px',
        'hover:z-3',
        'focus:outline-2',
        'focus:outline-primary-500',
        'focus:outline-solid',
        'focus:-outline-offset-4',
        'focus:[&_td]:bg-transparent'
      ]
    }
  }
})

const isElementInteractive = (element: Element | null): boolean => {
  if (!element) return false
  if (element.closest('button, a, input, select, textarea')) return true
  if (element.getAttribute('role') === 'button') return true
  return false
}

export const DataTableRow = ({ row, rowAction }: DataTableRowProps) => {
  const { enableRowSelection, disabledRows, getCanSomeRowsExpand } =
    useDataTable()

  const isDisabled = !!disabledRows?.[row.id]

  const toggleExpandHandler = row.getToggleExpandedHandler()
  const toggleSelectHandler = row.getToggleSelectedHandler()

  const getCheckedState = (): boolean | 'indeterminate' => {
    if (row.getIsSomeSelected()) return 'indeterminate'
    return row.getIsSelected()
  }

  const handleRowClick = (
    rowData: Row<Record<string, unknown>>,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => {
    if (!rowAction) return

    // Skip if clicking on an interactive element
    const rowElement = (event.target as Element).closest('tr')
    if (!rowElement || isElementInteractive(event.target as Element)) {
      return
    }

    rowAction(rowData.original, event)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (isElementInteractive(event.target as Element)) {
        return
      }
      event.preventDefault()
      handleRowClick(
        row,
        event as unknown as React.MouseEvent<HTMLTableRowElement>
      )
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      const currentRow = (event.target as HTMLElement).closest('tr')
      const nextRow = currentRow?.nextElementSibling as HTMLElement
      if (nextRow && nextRow.tagName === 'TR') {
        nextRow.focus()
      }
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      const currentRow = (event.target as HTMLElement).closest('tr')
      const previousRow = currentRow?.previousElementSibling as HTMLElement
      if (previousRow && previousRow.tagName === 'TR') {
        previousRow.focus()
      }
    }
  }

  return (
    <StyledRow
      isSelected={row.getIsSelected()}
      isDisabled={isDisabled}
      isFocusable={!!rowAction && !isDisabled}
      {...(rowAction && {
        onClick: (event) => handleRowClick(row, event),
        tabIndex: 0,
        className: 'cursor-pointer',
        onKeyDown: handleKeyDown
      })}
    >
      {getCanSomeRowsExpand() && (
        <Table.Cell
          data-testid={`expand-icon-${row.id}`}
          onClick={toggleExpandHandler}
          className={cn(
            'w-10',
            row.getCanExpand() ? 'cursor-pointer' : 'cursor-auto'
          )}
        >
          {row.getCanExpand() && (
            <Icon is={row.getIsExpanded() ? ChevronDown : ChevronRight} />
          )}
        </Table.Cell>
      )}

      {enableRowSelection && (
        <Table.Cell className="w-10">
          <DataTableRowSelectionCheckbox
            row={row}
            checked={getCheckedState()}
            onCheckedChange={toggleSelectHandler}
          />
        </Table.Cell>
      )}
      {row.getVisibleCells().map((cell) => {
        return <DataTableDataCell key={cell.id} cell={cell} />
      })}
    </StyledRow>
  )
}
